export type LinkScheme = 'https://' | 'tel:' | 'mailto:' | 'sms:';

export const LINK_SCHEME_OPTIONS: { value: LinkScheme; label: string; placeholder: string }[] = [
  { value: 'https://', label: 'Website', placeholder: 'example.com' },
  { value: 'tel:', label: 'Phone', placeholder: '+15551234567' },
  { value: 'mailto:', label: 'Email', placeholder: 'name@example.com' },
  { value: 'sms:', label: 'SMS', placeholder: '+15551234567' },
];

/**
 * Builds a footer/compliance link value from a selected scheme and a typed
 * value. Any scheme the user typed themselves (even repeated, e.g.
 * "tel:tel:+1...") is stripped first so the link never carries a duplicate
 * or conflicting prefix.
 */
export const composeLinkUrl = (scheme: LinkScheme, value: string): string => {
  const schemePattern = /^(https?:\/\/|tel:|mailto:|sms:)/i;
  let stripped = value.trim();
  while (schemePattern.test(stripped)) {
    stripped = stripped.replace(schemePattern, '');
  }
  return `${scheme}${stripped}`;
};

/**
 * Splits a stored footer/compliance link value back into its scheme and the
 * user-editable remainder, so an existing link can be loaded into the
 * scheme-selector form. `http://` and `https://` both map to the Website
 * scheme; values with no recognised scheme default to Website.
 */
export const decomposeLinkUrl = (value: string): { scheme: LinkScheme; value: string } => {
  const trimmed = value.trim();
  const match = trimmed.match(/^(https?:\/\/|tel:|mailto:|sms:)/i);
  if (!match) {
    return { scheme: 'https://', value: trimmed };
  }
  const prefix = match[1].toLowerCase();
  const scheme: LinkScheme = prefix.startsWith('http') ? 'https://' : (prefix as LinkScheme);
  return { scheme, value: trimmed.slice(match[1].length) };
};

const WEB_URL_PREFIX = /^(?:https?:\/\/|www\.)+/i;

/**
 * Removes any leading `https://`, `http://` and `www.` (in any combination,
 * e.g. a pasted "https://www.") so inputs with a fixed `https://` prefix only
 * hold the domain and path.
 */
export const stripUrlPrefix = (value: string): string => value.trim().replace(WEB_URL_PREFIX, '');

/**
 * Normalizes a typed/pasted web address to `https://domain.com/path`: drops
 * any scheme and `www.`, lowercases the host, and keeps the path/query.
 * Returns '' for blank input.
 */
export const toHttpsUrl = (value: string): string => {
  const rest = stripUrlPrefix(value);
  if (!rest) return '';
  const hostEnd = rest.search(/[/?#]/);
  const host = hostEnd === -1 ? rest : rest.slice(0, hostEnd);
  const path = hostEnd === -1 ? '' : rest.slice(hostEnd);
  return `https://${host.toLowerCase()}${path}`;
};

/**
 * Strips tracking parameters from a URL while keeping essential parameters
 * @param url - The URL to clean
 * @param keepParams - Array of parameter names to keep (defaults to tenant_id and mls_osn)
 * @returns The cleaned URL
 */
export const stripTrackingParams = (
  url: string,
  keepParams: string[] = ['tenant_id', 'mls_osn']
): string => {
  try {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);

    // Create a new URLSearchParams with only the parameters we want to keep
    const cleanedParams = new URLSearchParams();

    keepParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        cleanedParams.append(param, value);
      }
    });

    // Reconstruct the URL
    urlObj.search = cleanedParams.toString();

    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL
    console.error('Error parsing URL:', error);
    return url;
  }
};
